import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/lib/api/admin';
export type {
  DailyActivityUser,
  PeriodActivityUser,
  KpiMetric,
  KpiSummary,
  UserReport,
  UserReportRow,
  UserReportTotals,
} from '@/lib/api/admin';

export const adminActivityKeys = {
  all: ['admin', 'activity'] as const,
  daily: (date: string, timezone: string, userIds?: string[]) =>
    [...adminActivityKeys.all, 'daily', date, timezone, ...(userIds?.length ? [userIds.slice().sort().join(',')] : [])] as const,
  period: (startDate: string, endDate: string, timezone: string, userIds?: string[]) =>
    [...adminActivityKeys.all, 'period', startDate, endDate, timezone, ...(userIds?.length ? [userIds.slice().sort().join(',')] : [])] as const,
  kpiSummary: (startDate: string, endDate: string, timezone: string, userIds?: string[]) =>
    [...adminActivityKeys.all, 'kpi-summary', startDate, endDate, timezone, ...(userIds?.length ? [userIds.slice().sort().join(',')] : [])] as const,
  userReport: (
    userId: string,
    startDate: string,
    endDate: string,
    timezone: string,
    granularity?: 'day' | 'week' | 'month'
  ) =>
    [
      ...adminActivityKeys.all,
      'user-report',
      userId,
      startDate,
      endDate,
      timezone,
      granularity ?? 'day',
    ] as const,
};

export function useAdminDailyActivity(date: string) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return useQuery({
    queryKey: adminActivityKeys.daily(date, timezone),
    queryFn: () => adminApi.getDailyActivity(date, timezone),
    enabled: !!date,
  });
}

export function useAdminPeriodActivity(startDate: string, endDate: string, userIds?: string[]) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const sortedUserIds = userIds?.length ? userIds.slice().sort() : userIds;

  return useQuery({
    queryKey: adminActivityKeys.period(startDate, endDate, timezone, userIds),
    queryFn: () => adminApi.getPeriodActivity(startDate, endDate, timezone, sortedUserIds),
    enabled: !!startDate && !!endDate,
  });
}

export function useAdminKpiSummary(startDate: string, endDate: string, userIds?: string[]) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const sortedUserIds = userIds?.length ? userIds.slice().sort() : userIds;

  return useQuery({
    queryKey: adminActivityKeys.kpiSummary(startDate, endDate, timezone, userIds),
    queryFn: () => adminApi.getKpiSummary(startDate, endDate, timezone, sortedUserIds),
    enabled: !!startDate && !!endDate,
  });
}

export function useAdminUserReport(
  userId: string,
  startDate: string,
  endDate: string,
  granularity?: 'day' | 'week' | 'month'
) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return useQuery({
    queryKey: adminActivityKeys.userReport(userId, startDate, endDate, timezone, granularity),
    queryFn: () => adminApi.getUserReport(userId, startDate, endDate, timezone, granularity),
    enabled: !!userId && !!startDate && !!endDate,
  });
}
